import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, ArrowUpRightIcon, CheckIcon, ChevronDownIcon, CircleIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { cn } from '../ui/cn';
import type { LaunchCheck, LaunchContext, LaunchStep } from '../../data/launch';
interface LaunchStepCardProps {
  step: LaunchStep;
  context: LaunchContext;
  done: boolean;
  onToggle: () => void;
}
export function LaunchStepCard({
  step,
  context,
  done,
  onToggle
}: LaunchStepCardProps) {
  const [open, setOpen] = useState(false);
  const checks = step.checks(context);
  const gaps = checks.filter(item => item.status === 'attention').length;
  return <Card className={cn('overflow-hidden transition-opacity duration-150 ease-out', done && 'opacity-60')} data-id="element-432" data-mp="1jv9gwfac02o8">
      <div className="flex items-start gap-3.5 p-4" data-id="element-433" data-mp="3grehpy8d2km">
        <button type="button" onClick={onToggle} aria-pressed={done} aria-label={done ? `Mark step ${step.number} as not done` : `Mark step ${step.number} as done`} className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold transition-colors duration-150 ease-out', done ? 'border-accent bg-accent text-white' : 'border-white/[0.14] text-zinc-400 hover:border-accent/50 hover:text-white')} data-id="element-434" data-mp="3k5g2vz2l7r0">
          {done ? <CheckIcon className="h-4 w-4" strokeWidth={2.5} data-id="element-435" data-mp="1seeyq31ot6ea" /> : step.number}
        </button>

        <div className="min-w-0 flex-1" data-id="element-436" data-mp="1oeo23iphbw1u">
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex w-full items-start justify-between gap-3 text-left" data-id="element-437" data-mp="226ugaz92nsyn">
            <span className="min-w-0" data-id="element-438" data-mp="2c5jzcvcpfjgh">
              <span className={cn('block text-[15px] font-semibold text-white', done && 'line-through')} data-id="element-439" data-mp="2c2ec7lnmyrne">
                {step.title}
              </span>
              <span className="mt-0.5 block text-[11.5px] font-medium uppercase tracking-wide text-zinc-600" data-id="element-440" data-mp="2e9naru4bpje8">
                {step.timing}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2.5" data-id="element-441" data-mp="2o69ugfkwy4eo">
              {!done && gaps > 0 && <span className="hidden rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-warn ring-1 ring-inset ring-amber-500/25 sm:inline" data-id="element-442" data-mp="2sxrwl6yoab18">
                  {gaps} to do
                </span>}
              <ChevronDownIcon className={cn('h-4 w-4 text-zinc-500 transition-transform duration-200 ease-out', open && 'rotate-180')} data-id="element-443" data-mp="31wlquqoj6pbm" />
            </span>
          </button>

          <p className="mt-2 text-[13px] leading-relaxed text-zinc-400" data-id="element-444" data-mp="1cjrxtzbkcrfk">{step.summary}</p>

          {open && <div className="mt-4 space-y-4 border-t border-white/[0.06] pt-4" data-id="element-445" data-mp="1hjed0eaychr0">
              <ol className="space-y-2" data-id="element-446" data-mp="19zc68apdn5wh">
                {step.instructions.map((item, index) => <li key={item} className="flex gap-3 text-[13px] leading-relaxed text-zinc-300" data-id="element-447" data-mp="157kbdnz6ue0j" data-mpi={index}>
                    <span className="shrink-0 font-medium tabular-nums text-zinc-600" data-id="element-448" data-mp="108hj744b429z">{index + 1}.</span>
                    {item}
                  </li>)}
              </ol>

              {step.warning && <div className="flex gap-2.5 rounded-xl bg-amber-500/[0.07] p-3.5 ring-1 ring-inset ring-amber-500/20" data-id="element-449" data-mp="1swddk26eli3l">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-warn" data-id="element-450" data-mp="1nzd3zhachx7q" />
                  <p className="text-[12.5px] leading-relaxed text-amber-100/80" data-id="element-451" data-mp="1ixxjprfzruqe">{step.warning}</p>
                </div>}

              {checks.length > 0 && <div data-id="element-452" data-mp="7kllbw3044bm">
                  <h4 className="text-[11.5px] font-medium uppercase tracking-wide text-zinc-600" data-id="element-453" data-mp="7lwsm5vnnu7h">Where you stand</h4>
                  <ul className="mt-2.5 space-y-2" data-id="element-454" data-mp="2nd4pzxi48ch">
                    {checks.map(item => <CheckRow key={item.label} check={item} data-id="element-455" data-mp="1rdd35711euw9" />)}
                  </ul>
                </div>}

              {(step.studioPath || step.linkUrl) && <div className="flex flex-wrap gap-2" data-id="element-456" data-mp="1hf5pax21osyh">
                  {step.studioPath && <Link to={step.studioPath} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-[12.5px] font-medium text-white transition-colors duration-150 ease-out hover:bg-accent/90" data-id="element-457" data-mp="1me8viktrh581">
                      {step.studioLabel}
                    </Link>}
                  {step.linkUrl && <a href={step.linkUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-2 text-[12.5px] font-medium text-zinc-300 transition-colors duration-150 ease-out hover:border-accent/40 hover:text-white" data-id="element-458" data-mp="258nwgfzmv7yi">
                      {step.linkLabel} <ArrowUpRightIcon className="h-3.5 w-3.5" data-id="element-459" data-mp="3gzva5bqvd0vj" />
                    </a>}
                </div>}
            </div>}
        </div>
      </div>
    </Card>;
}
function CheckRow({
  check
}: {
  check: LaunchCheck;
}) {
  const icon = check.status === 'ready' ? <CheckIcon className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2.5} data-id="element-460" data-mp="2kanm9x2s2fog" /> : check.status === 'attention' ? <AlertTriangleIcon className="h-3.5 w-3.5 text-warn" data-id="element-461" data-mp="2y22ix6plyfdz" /> : <CircleIcon className="h-3 w-3 text-zinc-600" data-id="element-462" data-mp="2ta0z8zq41mdx" />;
  return <li className="flex gap-2.5" data-id="element-463" data-mp="2t8psqs5a60j4">
      <span className="mt-[3px] shrink-0" data-id="element-464" data-mp="25k6msj9pdzu7">{icon}</span>
      <span className="min-w-0" data-id="element-465" data-mp="25ivhug4ium1i">
        <span className="block text-[12.5px] font-medium text-zinc-200" data-id="element-466" data-mp="21jwgt6lwv9fc">{check.label}</span>
        <span className="block text-[12px] leading-relaxed text-zinc-500" data-id="element-467" data-mp="4j0bwhl99i86">{check.detail}</span>
      </span>
    </li>;
}